/* /professionals — Public listing page.
 *
 * Browseable directory of verified Mitras (architects, structural engineers,
 * suppliers, finance experts, etc.). Includes category chips, city +
 * minimum-experience filters and SEO-friendly per-pro detail links.
 *
 * Routing:
 *   • `/professionals` (legacy) → no Mitra bucket, shows the entire
 *     design-mitra catalogue with the original "Hire Professionals" hero
 *     to preserve historical CTAs.
 *   • `/mitras/:bucket` → same component, scoped to a single Mitra
 *     ecosystem bucket. The hero, category chips and empty-state are all
 *     rebranded around the bucket's identity (Design / Construction /
 *     Property / Material / Finance Mitra).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Search,
  MapPin,
  Briefcase,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Hammer,
} from "lucide-react";
import {
  PRO_CATEGORIES,
  CategoryIcon,
  VerifiedBadge,
  FeaturedBadge,
  categoryLabel,
} from "@/professionals/categories";
import { MITRA_BUCKET_BY_KEY } from "@/mitras/mitraCategories";
import { FINANCE_MITRA_ENABLED } from "@/lib/featureFlags";
import { useMitraSeo } from "@/mitras/useMitraSeo";
import BrandLogo from "@/components/BrandLogo";
import { FaWhatsapp } from "react-icons/fa";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 12;

const ProCard = ({ pro }) => {
  const meta = PRO_CATEGORIES.find((c) => c.key === pro.category);
  const photo = pro.profile_photo_url;
  const initials = pro.full_name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  return (
    <Link
      to={`/professionals/${pro.id}`}
      data-testid={`pro-card-${pro.id}`}
      className="group block bg-white rounded-3xl border-2 border-border hover:border-gold/60 hover:shadow-lg transition-all overflow-hidden"
    >
      <div className="p-5 flex items-start gap-4">
        <div
          className="h-16 w-16 rounded-2xl shrink-0 grid place-items-center text-white font-extrabold text-[18px] tracking-wide bg-navy border-2 border-gold/40 overflow-hidden"
          aria-hidden
        >
          {photo ? (
            <img src={photo} alt="" className="h-full w-full object-cover" />
          ) : (
            initials
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-[15.5px] font-extrabold tracking-tight text-navy truncate">
              {pro.full_name}
            </h3>
            <VerifiedBadge />
            {pro.featured && <FeaturedBadge />}
          </div>
          <div className="mt-1 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider" style={{ color: meta?.accent || "#0b2545" }}>
            <CategoryIcon category={pro.category} className="h-3.5 w-3.5" />
            {categoryLabel(pro.category)}
          </div>
          <div className="mt-2 text-[12.5px] text-muted-foreground space-y-0.5">
            <div className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" /> {pro.city}, {pro.state}
            </div>
            <div className="inline-flex items-center gap-1">
              <Briefcase className="h-3.5 w-3.5" /> {pro.experience_years} years experience
            </div>
          </div>
          <p className="mt-2 text-[13px] text-foreground/85 line-clamp-2 leading-snug">
            {pro.bio}
          </p>
        </div>
      </div>
      <div className="px-5 py-3 bg-navy/[0.03] border-t border-border flex items-center justify-between text-[12px] font-bold text-navy">
        View profile
        <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
};

const CategoryChip = ({ active, label, Icon, accent, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-pressed={active}
    className={`flex items-center gap-2 px-3.5 py-2 rounded-2xl border-2 text-[12.5px] font-bold transition-all whitespace-nowrap ${
      active
        ? "bg-navy text-gold border-navy shadow-md"
        : "bg-white text-navy border-border hover:border-navy"
    }`}
    style={active && accent ? { borderColor: accent, backgroundColor: accent } : undefined}
  >
    {Icon ? <Icon className="h-3.5 w-3.5" /> : <Hammer className="h-3.5 w-3.5" />}
    {label}
  </button>
);

const Professionals = () => {
  // When mounted under `/mitras/:bucket` (e.g. `/mitras/design`), scope
  // the listing to that Mitra ecosystem bucket. The legacy
  // `/professionals` route mounts the same component without a bucket
  // and behaves as before.
  const { bucket: bucketKey } = useParams();
  const navigate = useNavigate();
  const bucket = bucketKey ? MITRA_BUCKET_BY_KEY[bucketKey] : null;

  // Compliance kill-switch: redirect /mitras/finance traffic back to
  // the hub. The bucket data + sub-categories remain intact in
  // mitraCategories.jsx for one-flag restoration.
  useEffect(() => {
    if (!FINANCE_MITRA_ENABLED && bucketKey === "finance") {
      navigate("/mitras", { replace: true });
    }
  }, [bucketKey, navigate]);

  const [category, setCategory] = useState("all");
  const [city, setCity] = useState("");
  const [minExp, setMinExp] = useState(0);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = bucket
      ? `${bucket.label} · Mera Naksha`
      : "Hire Design Professionals · Mera Naksha";
  }, [bucket]);

  // SEO metadata + JSON-LD (Organization / LocalBusiness / Breadcrumb).
  // Bucket-aware: per-bucket title, description, keywords and services.
  useMitraSeo(
    bucket
      ? {
          title: `${bucket.label} on Mera Naksha — verified ${bucket.label}s across Bharat`,
          description: `${bucket.tagline} Browse verified ${bucket.label}s in your city, compare ratings and request a consultation — all on Mera Naksha.`,
          path: `/mitras/${bucket.key}`,
          bucket,
          breadcrumbs: [
            { label: "Home", href: "/" },
            { label: "Mitras", href: "/mitras" },
            { label: bucket.label, href: `/mitras/${bucket.key}` },
          ],
        }
      : {
          title: "Hire Verified Design Professionals · Mera Naksha",
          description:
            "Browse architects, structural engineers, MEP engineers and interior designers across India. Verified by Team Mera Naksha.",
          path: "/professionals",
          breadcrumbs: [
            { label: "Home", href: "/" },
            { label: "Hire Professionals", href: "/professionals" },
          ],
        }
  );

  // Reset filters whenever the URL bucket changes (e.g. when the user
  // navigates from /mitras/design → /mitras/construction directly).
  useEffect(() => {
    setCategory("all");
    setPage(1);
  }, [bucketKey]);

  // The sub-category chips shown above the listing. For the legacy route
  // we keep the original PRO_CATEGORIES (architect/structural/mep/interior).
  // For Mitra-bucket views we only show sub-categories belonging to that
  // bucket — keeping the chip strip clean and bucket-relevant.
  const visibleSubCategories = useMemo(() => {
    // Compliance: hide finance-related sub-categories from the public
    // listing while FINANCE_MITRA_ENABLED is false. DB and labels are
    // preserved — only the chip strip is filtered.
    const FINANCE_SUB_KEYS = new Set([
      "home_loan",
      "construction_loan",
      "emi_guidance",
      "bank_coordination",
    ]);
    const dropFinance = (list) =>
      FINANCE_MITRA_ENABLED ? list : list.filter((c) => !FINANCE_SUB_KEYS.has(c.key));
    if (!bucket) return dropFinance(PRO_CATEGORIES);
    const allowed = new Set(bucket.subCategoryKeys);
    return dropFinance(PRO_CATEGORIES.filter((c) => allowed.has(c.key)));
  }, [bucket]);

  useEffect(() => {
    let cancel = false;
    const params = new URLSearchParams();
    if (bucket) params.set("mitra_category", bucket.key);
    if (category !== "all") params.set("category", category);
    if (city.trim()) params.set("city", city.trim());
    if (minExp > 0) params.set("min_experience", String(minExp));
    if (q.trim()) params.set("q", q.trim());
    params.set("page", String(page));
    params.set("page_size", String(PAGE_SIZE));
    setLoading(true);
    axios
      .get(`${API}/professionals?${params.toString()}`)
      .then(({ data }) => {
        if (cancel) return;
        setData(data);
      })
      .catch((err) => {
        if (!cancel) {
          console.warn("Mitra listing fetch failed:", err?.message || err);
          setData({ items: [], total: 0 });
        }
      })
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [bucket, category, city, minExp, q, page]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const onChangeCategory = (k) => {
    setCategory(k);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="professionals-page">
      <header className="bg-navy text-white border-b-4 border-gold">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" data-testid="pros-back-home" className="inline-flex items-center bg-white rounded-lg px-2 py-1 shadow-sm">
            <BrandLogo size="sm" asLink={false} withTagline lang="en" testId="pros-brand-logo" />
          </Link>
          {bucket && (
            <Link
              to="/mitras"
              data-testid="pros-back-to-hub"
              className="hidden sm:inline-flex items-center gap-1.5 text-[12px] text-white/80 hover:text-gold"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> All Mitra categories
            </Link>
          )}
          <div className="hidden sm:flex items-center gap-2 text-[12px] text-white/80">
            {bucket ? `Are you a ${bucket.label}?` : "Are you a design professional?"}
            <Link
              to="/professional/register"
              data-testid="pros-cta-register"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gold text-navy font-extrabold hover:bg-gold-dark hover:text-white"
            >
              {bucket ? "Become a Mitra" : "Join as a Professional"} <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 pb-7 pt-2">
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gold/15 border border-gold/30 text-gold text-[10.5px] font-bold tracking-widest uppercase mb-3"
            data-testid="pros-hero-badge"
          >
            <Sparkles className="h-3 w-3" />
            {bucket ? bucket.labelHi : "Hire Professionals"}
          </div>
          <h1
            className="text-2xl sm:text-4xl font-extrabold tracking-tight leading-tight"
            data-testid="pros-hero-title"
          >
            {bucket
              ? bucket.label
              : "Connect with Verified Design Professionals"}
          </h1>
          <p className="mt-1.5 text-[14px] sm:text-base text-white/80 max-w-2xl">
            {bucket
              ? bucket.description
              : "Architecture, engineering and interior support — vetted by Team Mera Naksha, available across Bharat."}
          </p>
          {bucket && (
            <div
              className="mt-3 flex flex-wrap gap-1.5"
              data-testid="pros-hero-services"
            >
              {bucket.services.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/10 text-white/90 text-[11.5px] font-bold border border-white/15"
                >
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* Category strip — narrowed to this bucket's sub-categories when scoped */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
          <CategoryChip
            active={category === "all"}
            label={bucket ? `All ${bucket.label}s` : "All categories"}
            onClick={() => onChangeCategory("all")}
            testId="pros-cat-all"
          />
          {visibleSubCategories.map((c) => (
            <CategoryChip
              key={c.key}
              active={category === c.key}
              label={c.label}
              Icon={c.Icon}
              accent={c.accent}
              onClick={() => onChangeCategory(c.key)}
              testId={`pros-cat-${c.key}`}
            />
          ))}
        </div>

        {/* Filter row */}
        <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_0.9fr] gap-3 items-end bg-white rounded-2xl border border-border p-3">
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-[0.18em] text-navy/70 font-bold">Search</span>
            <div className="mt-1 relative">
              <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="Name or speciality"
                data-testid="pros-search"
                className="w-full h-11 pl-9 pr-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-[0.18em] text-navy/70 font-bold">City</span>
            <div className="mt-1 relative">
              <MapPin className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={city}
                onChange={(e) => { setCity(e.target.value); setPage(1); }}
                placeholder="e.g. Patna"
                data-testid="pros-city"
                className="w-full h-11 pl-9 pr-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-[0.18em] text-navy/70 font-bold">Min experience</span>
            <select
              value={minExp}
              onChange={(e) => { setMinExp(Number(e.target.value)); setPage(1); }}
              data-testid="pros-min-experience"
              className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
            >
              <option value={0}>Any</option>
              <option value={3}>3+ years</option>
              <option value={5}>5+ years</option>
              <option value={10}>10+ years</option>
              <option value={15}>15+ years</option>
            </select>
          </label>
        </div>

        {/* Result count */}
        <div className="flex items-center justify-between gap-3 text-[12.5px] text-muted-foreground">
          <span>
            <strong className="text-navy">{data.total}</strong> verified{" "}
            {bucket
              ? data.total === 1
                ? `${bucket.label}`
                : `${bucket.label}s`
              : data.total === 1
              ? "professional"
              : "professionals"}{" "}
            found
            {category !== "all" && ` in ${categoryLabel(category)}`}
            {city && ` near ${city}`}
          </span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pros-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`skel-${i}`} className="rounded-3xl bg-white border-2 border-border p-5 animate-pulse h-44" />
            ))}
          </div>
        ) : data.items.length === 0 ? (
          <div className="rounded-3xl bg-white border-2 border-dashed border-border p-10 text-center" data-testid="pros-empty">
            <Briefcase className="h-10 w-10 mx-auto text-navy/30" />
            <div className="mt-3 font-bold text-navy">
              {bucket
                ? `No ${bucket.label}s listed yet`
                : "No professionals match yet"}
            </div>
            <div className="text-[13px] text-muted-foreground mt-1 max-w-md mx-auto">
              {bucket
                ? `We're actively onboarding verified ${bucket.label}s across Bharat. Be one of the first — or submit an inquiry and Team Mera Naksha will route it personally.`
                : "We're onboarding more verified professionals across India every week. Try a wider city or another category — or submit a direct inquiry and Team Mera Naksha will route it."}
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              {bucket && (
                <Link
                  to="/professional/register"
                  data-testid="pros-empty-register"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-navy text-gold font-extrabold text-[13px] hover:bg-navy-dark"
                >
                  Become a {bucket.label}
                </Link>
              )}
              <Link
                to="/inquiry/new"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gold text-navy font-extrabold text-[13px] hover:bg-gold-dark hover:text-white"
              >
              Submit a custom request <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.items.map((p) => (
              <ProCard key={p.id} pro={p} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {data.total > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              data-testid="pros-prev"
              className="h-9 px-3 rounded-lg bg-white border border-border text-navy font-bold text-[12.5px] inline-flex items-center gap-1 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" /> Prev
            </button>
            <div className="text-[12.5px] text-muted-foreground">
              Page <strong className="text-navy">{page}</strong> of <strong>{totalPages}</strong>
            </div>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              data-testid="pros-next"
              className="h-9 px-3 rounded-lg bg-white border border-border text-navy font-bold text-[12.5px] inline-flex items-center gap-1 disabled:opacity-50"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Trust strip */}
        <section className="rounded-3xl bg-navy text-white p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-gold font-bold">
              Architecture, Engineering &amp; Interior Support
            </div>
            <div className="mt-1 text-lg font-extrabold tracking-tight">
              Professional Planning for Better Homes
            </div>
          </div>
          <div className="text-[13px] text-white/80 leading-relaxed sm:col-span-2">
            Every professional on Mera Naksha is verified by Team Mera Naksha before
            their profile goes public. We never share your number publicly — inquiries
            are routed through us. Customised design support, on your timeline.
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-border p-4 flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-500 text-white grid place-items-center shrink-0">
            <FaWhatsapp className="h-5 w-5" />
          </div>
          <div className="text-[13px]">
            <div className="font-bold text-navy">Talk to Team Mera Naksha</div>
            <div className="text-muted-foreground">
              Not sure which professional you need? WhatsApp{" "}
              <a href="https://wa.me/919717667115" className="text-navy font-bold" target="_blank" rel="noopener noreferrer">
                +91 97176 67115
              </a>{" "}
              and we'll guide you in under an hour.
            </div>
          </div>
        </section>

        {loading && (
          <div className="inline-flex items-center gap-2 text-[12px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Refreshing…
          </div>
        )}
      </main>
    </div>
  );
};

export default Professionals;
